"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidConnectFlow/steps/PlaidManualUploadStep";

import React, { useEffect, useCallback } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { Track } from "@/src/lib/tracking";
import type { UploadedAttachmentFile } from "@/src/utils/attachmentUpload/uploadAttachmentFiles";
import type { SenderAttachment } from "@components/Sender/type";
import { usePlaidManualUpload } from "../hooks/usePlaidManualUpload";
import { PlaidUploadedDocumentChip } from "../shared/PlaidUploadedDocumentChip";
import { PlaidDashedUploadButton } from "../shared/PlaidDashedUploadButton";

const I18n = I18nUtil.createScoped(Translations);

export interface PlaidManualUploadStepProps {
  onConfirm: (attachments: SenderAttachment[]) => void;
  onBack?: () => void;
  onClose?: () => void;
  initialFiles?: UploadedAttachmentFile[];
  isSubmitting?: boolean;
}

export const PlaidManualUploadStep: React.FC<PlaidManualUploadStepProps> = ({
  onConfirm,
  onBack,
  onClose,
  initialFiles = [],
  isSubmitting = false,
}) => {
  const {
    rawFiles,
    uploadedFiles,
    isUploading,
    fileInputRef,
    openFilePicker,
    handleFileInputChange,
    removeFile,
  } = usePlaidManualUpload(initialFiles);

  useEffect(() => {
    Track.view("plaid_manual_upload_view");
  }, []);

  const handleConfirm = useCallback(() => {
    if (uploadedFiles.length === 0 || isUploading || isSubmitting) {
      return;
    }
    Track.event("plaid_manual_upload_confirm", {
      file_count: uploadedFiles.length,
    });
    onConfirm(uploadedFiles);
  }, [uploadedFiles, isUploading, isSubmitting, onConfirm]);

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-background_solid_secondary text-content_primary">
      {/* Hidden file input for file selection */}
      <input
        type="file"
        ref={fileInputRef}
        multiple
        accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls"
        className="hidden"
        onChange={handleFileInputChange}
        data-testid="plaid-manual-upload-input"
      />

      {/* Top Nav Header — fixed outside the scroll area */}
      <div className="flex items-center justify-between w-full shrink-0 min-h-[44px] px-4 pt-3 md:px-6 md:pt-4">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            data-openreplay-label="plaidconnectflow_step_back"
            className="size-10 rounded-full bg-background_solid_primary shadow-[0px_2px_10px_rgba(0,0,0,0.08)] flex items-center justify-center text-content_primary hover:opacity-90 active:scale-95 transition-all cursor-pointer"
            aria-label={I18n.t("Go back")}
          >
            <Icon name="left-arrow-alt" size={20} />
          </button>
        ) : (
          <div className="size-10" />
        )}

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            data-openreplay-label="plaidconnectflow_step_close"
            className="size-10 rounded-full bg-background_solid_primary shadow-[0px_2px_10px_rgba(0,0,0,0.08)] flex items-center justify-center text-content_primary hover:opacity-90 active:scale-95 transition-all cursor-pointer"
            aria-label={I18n.t("Close")}
          >
            <Icon name="close" size={20} />
          </button>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-3 md:px-6 md:pb-4">
        <div className="flex flex-col w-full max-w-[400px] mx-auto">
          {/* Intro Block */}
          <div className="flex flex-col gap-2 pt-4 pb-6">
            <h1 className="font-['Lexend'] text-2xl font-normal text-content_primary leading-8 tracking-[-0.96px]">
              {I18n.t("Upload what you have")}
            </h1>
            <p className="text-sm text-content_secondary leading-5 font-normal">
              {I18n.t(
                "Drop in whatever you've got — statements, holdings, an advisor's proposal. It stays private, and I'll make sense of it.",
              )}
            </p>
          </div>

          {/* What I Can Read Guide Card */}
          <div className="flex flex-col gap-3 mb-6">
            <span className="text-xs font-medium text-content_tertiary">
              {I18n.t("What I can read")}
            </span>
            {/* 不加 overflow-hidden：APP WebView 对「border+rounded+overflow-hidden 同元素」按方形盒裁剪，
                背景在四角盖掉边框弧段（线不连续）；子行纯静态堆叠无溢出，裁剪无承载功能（web 视觉零变化） */}
            <div className="bg-background_solid_primary rounded-3 border border-border_main flex flex-col shadow-sm">
              {/* Row 1: Statement PDF */}
              <div className="flex items-center gap-3 p-3.5 sm:p-4 border-b border-border_main">
                <div className="size-11 rounded-2 bg-background_brand_overlay10 flex items-center justify-center shrink-0">
                  <img
                    src="/assets/images/plaid/manual-upload/guide-pdf.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-6 w-6"
                  />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-semibold text-content_primary leading-5">
                    {I18n.t("Statement PDF")}
                  </p>
                  <p className="text-xs text-content_tertiary leading-4 mt-0.5">
                    {I18n.t("Monthly or quarterly, last 90 days")}
                  </p>
                </div>
              </div>

              {/* Row 2: Holdings screenshot */}
              <div className="flex items-center gap-3 p-3.5 sm:p-4 border-b border-border_main">
                <div className="size-11 rounded-2 bg-background_brand_overlay10 flex items-center justify-center shrink-0">
                  <img
                    src="/assets/images/plaid/manual-upload/guide-landscape.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-6 w-6"
                  />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-semibold text-content_primary leading-5">
                    {I18n.t("Holdings screenshot")}
                  </p>
                  <p className="text-xs text-content_tertiary leading-4 mt-0.5">
                    {I18n.t("From your broker app, positions visible")}
                  </p>
                </div>
              </div>

              {/* Row 3: CSV export */}
              <div className="flex items-center gap-3 p-3.5 sm:p-4">
                <div className="size-11 rounded-2 bg-background_brand_overlay10 flex items-center justify-center shrink-0">
                  <img
                    src="/assets/images/plaid/manual-upload/guide-csv.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-6 w-6"
                  />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-semibold text-content_primary leading-5">
                    {I18n.t("CSV export")}
                  </p>
                  <p className="text-xs text-content_tertiary leading-4 mt-0.5">
                    {I18n.t("Positions or transactions export")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Your Documents List (when items exist) */}
          {rawFiles.length > 0 && (
            <div className="flex flex-col gap-2.5 mb-5">
              <span className="text-xs font-medium text-content_tertiary">
                {I18n.t("Your documents")}
              </span>
              <div className="flex flex-col gap-2">
                {rawFiles.map((file) => (
                  <PlaidUploadedDocumentChip
                    key={file.id}
                    file={file}
                    onRemove={() => removeFile(file.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Sticky Action Area */}
      <div className="w-full max-w-[400px] mx-auto flex flex-col gap-3 pt-4 pb-5 shrink-0 px-4 md:px-6 md:pb-6">
        <PlaidDashedUploadButton
          onClick={openFilePicker}
          disabled={rawFiles.length >= 10}
          label={I18n.t("Upload documents")}
          variant="empty"
        />

        <button
          type="button"
          disabled={uploadedFiles.length === 0 || isUploading || isSubmitting}
          onClick={handleConfirm}
          data-openreplay-label="plaidconnectflow_upload_confirm"
          className="w-full h-12 md:h-11 rounded-lg bg-background_brand_overlay100 text-content_inverse_primary font-medium text-base hover:opacity-90 active:scale-[0.99] transition-all cursor-pointer flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <Icon name="Spinner" size={18} className="animate-spin mr-2" />
          ) : null}
          {I18n.t("Confirm")}
        </button>
      </div>
    </div>
  );
};
